import { IMAGE_THUMBNAIL_QUALITY, IMAGE_THUMBNAIL_SIZE } from '@/constants/file.constants';
import { MAX_UPLOAD_SIZE_THUMBNAIL } from 'common/constants/file.upload.limits';
import { ALLOWED_FILE_TYPES_THUMBNAIL } from 'common/constants/file.upload.types';

/**
 * Square thumbnail drawn by the browser, so the server never decodes images. Crops the centre like
 * `object-cover`, applies EXIF orientation and drops all metadata. Returns null for other files, images the
 * browser cannot convert and when the thumbnail would not be smaller than the original.
 */
export async function createImageThumbnail(file: File): Promise<File | null> {
  if (!file.type.startsWith('image/')) return null;
  let bitmap: ImageBitmap | undefined;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const side = Math.min(bitmap.width, bitmap.height);
    const size = Math.min(IMAGE_THUMBNAIL_SIZE, side);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    if (!context) return null;
    // JPEG has no alpha channel; transparent pixels would otherwise turn black.
    context.fillStyle = 'white';
    context.fillRect(0, 0, size, size);
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, size, size);
    // In preference order. Safari cannot encode WebP and silently returns a PNG, so it falls through to JPEG.
    for (const type of ALLOWED_FILE_TYPES_THUMBNAIL) {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, IMAGE_THUMBNAIL_QUALITY));
      if (blob?.type !== type) continue;
      if (blob.size >= file.size || blob.size > MAX_UPLOAD_SIZE_THUMBNAIL) return null;
      return new File([blob], `thumbnail.${type.split('/')[1]}`, { type });
    }
    return null;
  } catch {
    return null;
  } finally {
    // The decoded full-size image can take hundreds of MB; release it on every path.
    bitmap?.close();
  }
}
